// General helper utilities

/**
 * Debounce a function
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: unknown[]) => void>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return function (...args: Parameters<T>) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

/**
 * Throttle a function
 * @param func - Function to throttle
 * @param limit - Time limit in milliseconds
 * @returns Throttled function
 */
export function throttle<T extends (...args: unknown[]) => void>(
    func: T,
    limit: number
): (...args: Parameters<T>) => void {
    let inThrottle = false;

    return function (...args: Parameters<T>) {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

/**
 * Generate a unique ID
 * @returns Unique identifier string
 */
export function generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Deep clone an object
 * @param obj - Object to clone
 * @returns Cloned object
 */
export function deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
}

/**
 * Check if value is defined (not null or undefined)
 * @param value - Value to check
 * @returns True if defined
 */
export function isDefined<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
}

/**
 * Wait for a specified time
 * @param ms - Time to wait in milliseconds
 * @returns Promise that resolves after wait time
 */
export function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Get URL parameter value
 * @param param - Parameter name
 * @returns Parameter value or null
 */
export function getUrlParam(param: string): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(param);
}

/**
 * Set URL parameter without page reload
 * @param param - Parameter name
 * @param value - Parameter value
 */
export function setUrlParam(param: string, value: string): void {
    const url = new URL(window.location.href);
    url.searchParams.set(param, value);
    window.history.pushState({}, '', url);
}

/**
 * Remove element from array
 * @param arr - Array to modify
 * @param item - Item to remove
 * @returns Modified array
 */
export function removeFromArray<T>(arr: T[], item: T): T[] {
    const index = arr.indexOf(item);
    if (index > -1) {
        arr.splice(index, 1);
    }
    return arr;
}

/**
 * Create element with classes
 * @param tag - HTML tag name
 * @param classes - CSS classes to add
 * @returns Created element
 */
export function createElement<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    classes: string[] = []
): HTMLElementTagNameMap[K] {
    const element = document.createElement(tag);
    element.classList.add(...classes);
    return element;
}

/**
 * Add event listener with cleanup
 * @param element - Element to attach listener to
 * @param event - Event name
 * @param handler - Event handler
 * @returns Cleanup function
 */
export function addListener<K extends keyof HTMLElementEventMap>(
    element: HTMLElement,
    event: K,
    handler: (ev: HTMLElementEventMap[K]) => void
): () => void {
    element.addEventListener(event, handler);
    return () => element.removeEventListener(event, handler);
}
